import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ElectronTitleBar.module.css';
import { shouldUseElectronChrome } from '../../config/runtime';
import { useI18n } from '../../i18n/I18nProvider';
import { DEFAULT_FLAG_SRC, LANGUAGE_OPTIONS } from '../../state';

const LANGUAGES = LANGUAGE_OPTIONS;


const LANG_DROPDOWN_W = 262;
const LANG_DROPDOWN_H = 249;
const LANG_EDGE_PAD = 6;

function getShell() {
  if (typeof window === 'undefined') return null;
  if (!shouldUseElectronChrome()) return null;
  return window.npcShell || null;
}

export default function ElectronTitleBar({
  logoSrc = '/assets/main_logo.webp',
  channelLabel = 'Night Poker Club',
  centerTitle = 'Night Network-Poker Club Service',
  showMusicButton = true,
  showLogo = true,
  showChannelLabel = true,
  showMusicSection = false,
  showLanguageButton = true,
  musicTitle = 'Lucky Night - Night Club Original Sing',
  musicIconSrc = '/assets/titlebar_music_note.svg',
  playIconSrc = '/assets/titlebar_play.svg',
  pauseIconSrc = '/assets/titlebar_pause.svg',
  useNativeWindowControls = false,
  onCloseRequest = null,
}) {
  const shell = getShell();
  const isElectron = shouldUseElectronChrome();
  const [muted, setMuted] = useState(false);
  const [closePending, setClosePending] = useState(false);

  const { lang, setLang, t } = useI18n();

  const [langOpen, setLangOpen] = useState(false);
  const langWrapRef = useRef(null);
  const langBtnRef = useRef(null);
  const langDropdownRef = useRef(null);
  const [langMenuPos, setLangMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isElectron) return undefined;

    let mounted = true;

    shell
      .getAudioMuted?.()
      .then((value) => {
        if (!mounted) return;
        setMuted(Boolean(value));
      })
      .catch(() => {});

    const unsubscribe = shell.onAudioMutedChanged?.((value) => {
      setMuted(Boolean(value));
    });

    return () => {
      mounted = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isElectron, shell]);

  useEffect(() => {
    const onDown = (e) => {
      if (!langOpen) return;

      const target = e.target;
      if (langWrapRef.current && langWrapRef.current.contains(target)) return;
      if (langDropdownRef.current && langDropdownRef.current.contains(target)) return;

      setLangOpen(false);
    };

    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [langOpen]);

  useLayoutEffect(() => {
    if (!langOpen) return;

    const update = () => {
      const r = langBtnRef.current?.getBoundingClientRect?.();
      if (!r) return;

      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;

      let top = r.bottom + LANG_EDGE_PAD;
      let left = r.right - LANG_DROPDOWN_W;

      left = Math.min(left, Math.max(LANG_EDGE_PAD, vw - LANG_DROPDOWN_W - LANG_EDGE_PAD));
      left = Math.max(LANG_EDGE_PAD, left);

      if (top + LANG_DROPDOWN_H + LANG_EDGE_PAD > vh) {
        top = r.top - LANG_EDGE_PAD - LANG_DROPDOWN_H;
      }

      top = Math.min(top, Math.max(LANG_EDGE_PAD, vh - LANG_DROPDOWN_H - LANG_EDGE_PAD));
      top = Math.max(LANG_EDGE_PAD, top);

      setLangMenuPos({ top, left });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [langOpen, lang]);

  const musicLabel = useMemo(() => (muted ? t('electron.music.play', null, '음악 재생') : t('electron.music.stop', null, '음악 중지')), [muted, t]);
  const shouldRenderMusicSection = Boolean(showMusicSection && musicTitle);

  const currentLanguage = useMemo(
    () => LANGUAGES.find((x) => x.code === lang) || LANGUAGES[0],
    [lang]
  );

  if (!isElectron) return null;

  const handleToggleAudio = async () => {
    try {
      const next = await shell.toggleAudioMuted?.();
      if (typeof next === 'boolean') setMuted(next);
    } catch (_) {}
  };

  const handleSelectLanguage = (code) => {
    setLang(code);
    setLangOpen(false);
  };

  return (
    <div className={styles.bar}>
      <div className={styles.dragLayer} />

      <div className={styles.left}>
        {showLogo && logoSrc ? (
          <img src={logoSrc} alt={t('common.logoAlt', null, 'logo')} className={styles.logo} draggable="false" />
        ) : null}

        {showChannelLabel && channelLabel ? (
          <span className={styles.channelLabel}>{channelLabel}</span>
        ) : null}
      </div>

      <div className={styles.center}>
        <span className={styles.centerTitle}>{centerTitle}</span>
      </div>

      <div className={styles.right}>
        {shouldRenderMusicSection ? (
          <div className={styles.musicPill}>
            <img
              src={musicIconSrc}
              alt=""
              className={styles.musicIcon}
              draggable="false"
            />

            <span className={styles.musicTitle}>{musicTitle}</span>

            <button
              type="button"
              className={styles.musicToggleButton}
              onClick={handleToggleAudio}
              aria-label={musicLabel}
              title={musicLabel}
            >
              <img
                src={muted ? playIconSrc : pauseIconSrc}
                alt=""
                className={styles.musicToggleSvg}
                draggable="false"
              />
            </button>
          </div>
        ) : showMusicButton ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={handleToggleAudio}
            aria-label={musicLabel}
            title={musicLabel}
          >
            {muted ? '🔇' : '🎵'}
          </button>
        ) : null}

        {showLanguageButton ? (
          <div className={styles.langWrap} ref={langWrapRef}>
            <button
              ref={langBtnRef}
              type="button"
              className={`${styles.langButton} ${langOpen ? styles.langButtonOpen : ''}`}
              onClick={() => setLangOpen((v) => !v)}
              aria-expanded={langOpen}
              aria-haspopup="menu"
              title={currentLanguage?.label || t('electron.language', null, 'Language')}
            >
              <span className={styles.langCode}>
                {String(currentLanguage?.code || 'en').toUpperCase()}
              </span>
              <img
                className={styles.langFlag}
                src={currentLanguage?.flagSrc || DEFAULT_FLAG_SRC}
                alt=""
                draggable="false"
              />
            </button>
          </div>
        ) : null}

        {useNativeWindowControls ? (
          <div className={styles.nativeWindowControlsSpacer} aria-hidden="true" />
        ) : (
          <>
            <button
              type="button"
              className={styles.windowButton}
              onClick={() => shell.minimize?.()}
              aria-label={t('electron.window.hide', null, '창 숨기기')}
              title={t('electron.window.hide', null, '창 숨기기')}
            >
              —
            </button>

            <button
              type="button"
              className={`${styles.windowButton} ${styles.closeButton}`}
              disabled={closePending}
              onClick={async () => {
                if (closePending) return;
                setClosePending(true);
                try {
                  if (typeof onCloseRequest === 'function') {
                    const result = await onCloseRequest();
                    if (result === false) return;
                  }
                  shell.close?.();
                } finally {
                  setClosePending(false);
                }
              }}
              aria-label={t('electron.window.close', null, '창 끄기')}
              title={t('electron.window.close', null, '창 끄기')}
            >
              {closePending ? '…' : '✕'}
            </button>
          </>
        )}
      </div>

      {langOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={langDropdownRef}
              className={styles.langDropdown}
              style={{
                top: langMenuPos.top,
                left: langMenuPos.left,
                width: LANG_DROPDOWN_W,
                maxHeight: LANG_DROPDOWN_H,
              }}
            >
              <div className={styles.langGrid}>
                {LANGUAGES.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className={`${styles.langItem} ${item.code === lang ? styles.langItemActive : ''}`}
                    onClick={() => handleSelectLanguage(item.code)}
                  >
                    <img
                      className={styles.langItemFlag}
                      src={item.flagSrc || DEFAULT_FLAG_SRC}
                      alt=""
                      draggable="false"
                    />
                    <span className={styles.langItemLabel}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
